import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';

import { extractErrorMessage } from '@/lib/api';
import { getLeads, getLeadStats, generateQuestionnaire, sendWelcomeMail, type LeadQueryParams } from '../services/leads.service';

export function useLeads(params?: LeadQueryParams) {
  return useQuery({
    queryKey: ['leads', params],
    queryFn: () => getLeads(params),
    placeholderData: keepPreviousData,
    // Global defaults (staleTime 5 min, refetchOnWindowFocus false) make the admin
    // waitlist look "frozen" while open. Override so new candidacies surface without
    // a manual full-page reload.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
}

export function useGenerateQuestionnaire() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => generateQuestionnaire(leadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useSendWelcomeMail() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (leadId: string) => sendWelcomeMail(leadId),
    onSuccess: (result) => {
      if (result.sent) {
        toast.success('Mail inviata');
      }
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useLeadStats() {
  return useQuery({
    queryKey: ['leads', 'stats'],
    queryFn: getLeadStats,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });
}
